import React, { useState } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { useAuthStore } from '../../store/authStore';
import { useHistory } from 'react-router-dom';
import Icon from '../../components/Icon';
import { showToast } from '../../components/Toast';

const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  React.useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return matches;
};

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isKidLogin, setIsKidLogin] = useState(false);
  const [kidName, setKidName] = useState('');
  const [familyCode, setFamilyCode] = useState('');
  const [passcode, setPasscode] = useState('');
  const { login, kidLogin } = useAuthStore();
  const history = useHistory();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const heroImage = isDesktop
    ? `${process.env.PUBLIC_URL}/login-bg.webp`
    : `${process.env.PUBLIC_URL}/login-bg-mobile.webp`;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      if (isKidLogin) {
        if (!kidName || !familyCode || !passcode) { setError('Please fill in all fields'); setIsLoading(false); return; }
        await kidLogin(kidName.trim(), familyCode.trim(), passcode);
        history.replace('/app/home');
      } else {
        if (!email || !password) { setError('Please fill in all fields'); setIsLoading(false); return; }
        await login(email.trim(), password);
        const { family } = useAuthStore.getState();
        history.replace(family ? '/app/home' : '/family-setup');
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Login failed. Please try again.';
      setError(msg);
      showToast('Login failed', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <IonPage>
      <IonContent fullscreen scrollY>
        <div className="fh-landing">
          <div
            className="fh-landing__photo"
            style={{ backgroundImage: `url(${heroImage})` }}
          />
          <div className="fh-landing__panel">
            <div className="fh-auth__inner">
              <div className="fh-auth__header">
                <img src="/icon-nobg.png" alt="FamHearth" className="fh-auth__icon" />
                <h1 className="fh-auth__title" style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontStyle: 'italic', fontSize: 44, lineHeight: 1 }}>
                  Welcome <span style={{ color: '#c5512d' }}>home.</span>
                </h1>
                <p className="fh-auth__subtitle">{isKidLogin ? 'Sign in as a kid' : 'Sign in to your family'}</p>
              </div>

              {/* Toggle between parent and kid login */}
              <div className="fh-segmented" role="tablist" aria-label="Choose account type">
                <button
                  type="button"
                  role="tab"
                  aria-selected={!isKidLogin}
                  onClick={() => { setIsKidLogin(false); setError(''); }}
                  className="fh-segmented__option"
                >
                  <span style={{ fontSize: 18 }}>👨‍👩‍👧</span>
                  Parent / Adult
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={isKidLogin}
                  onClick={() => { setIsKidLogin(true); setError(''); }}
                  className="fh-segmented__option fh-segmented__option--alt"
                >
                  <span style={{ fontSize: 18 }}>🧒</span>
                  Kid
                </button>
              </div>

              <form onSubmit={handleLogin} aria-label="Login form" className="fh-auth__form">
                {isKidLogin ? (
                  <>
                    <div className="fh-auth__field">
                      <label className="fh-auth__label">Your Name</label>
                      <input
                        type="text"
                        value={kidName}
                        onChange={(e) => setKidName(e.target.value)}
                        autoComplete="name"
                        placeholder="e.g. Oluwatobi"
                        className="fh-auth__input"
                      />
                    </div>
                    <div className="fh-auth__field">
                      <label className="fh-auth__label">Family Code</label>
                      <input
                        type="text"
                        value={familyCode}
                        onChange={(e) => setFamilyCode(e.target.value.toUpperCase())}
                        autoComplete="off"
                        placeholder="e.g. ABC123"
                        className="fh-auth__input"
                        style={{ textTransform: 'uppercase', letterSpacing: '0.1em' }}
                        maxLength={8}
                      />
                    </div>
                    <div className="fh-auth__field">
                      <label className="fh-auth__label">Passcode</label>
                      <input
                        type="password"
                        value={passcode}
                        onChange={(e) => setPasscode(e.target.value)}
                        autoComplete="off"
                        placeholder="Enter your passcode"
                        className="fh-auth__input"
                        inputMode="numeric"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="fh-auth__field">
                      <label className="fh-auth__label">Email</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                        inputMode="email"
                        placeholder="you@example.com"
                        className="fh-auth__input"
                      />
                    </div>
                    <div className="fh-auth__field">
                      <label className="fh-auth__label">Password</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoComplete="current-password"
                          placeholder="Enter your password"
                          className="fh-auth__input"
                          style={{ paddingRight: 48 }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          style={{
                            position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                            background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                            minWidth: 44, minHeight: 44,
                          }}
                        >
                          <Icon name={showPassword ? 'visibility_off' : 'visibility'} size={20} color="rgba(255,255,255,0.35)" />
                        </button>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                        <button
                          type="button"
                          onClick={() => history.push('/forgot-password')}
                          className="fh-auth__link"
                          style={{ fontSize: 13, minHeight: 32, padding: '4px 0' }}
                        >
                          Forgot password?
                        </button>
                      </div>
                    </div>
                  </>
                )}
                {error && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: 'rgba(239,83,80,0.08)',
                    border: '1px solid rgba(239,83,80,0.2)',
                    borderRadius: 12, padding: '10px 14px',
                  }}>
                    <Icon name="warning" size={18} color="#ef5350" />
                    <p style={{ color: '#ef5350', fontSize: 13, fontWeight: 600, margin: 0 }}>{error}</p>
                  </div>
                )}
                <button type="submit" disabled={isLoading} className={`fh-auth__submit${isKidLogin ? ' fh-auth__submit--kid' : ''}`} style={{ minHeight: 44 }}>
                  {isLoading ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      <span style={{
                        width: 18, height: 18, borderRadius: '50%',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderTopColor: '#fff',
                        animation: 'fh-spin 0.6s linear infinite',
                        display: 'inline-block',
                      }} />
                      Signing in...
                    </span>
                  ) : 'Sign In'}
                </button>
              </form>

              <p className="fh-auth__footer">
                Don't have an account?{' '}
                <button onClick={() => history.push('/register')} className="fh-auth__link" style={{ minHeight: 44 }}>
                  Sign up
                </button>
              </p>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default LoginPage;
